import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import ProductImage from '../components/ui/ProductImage.jsx';
import VerifyEmailBanner from '../components/ui/VerifyEmailBanner.jsx';
import { useCart } from '../context/CartContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { createOrder, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from '../api/ordersApi.js';
import { getAddresses, getPoints } from '../api/accountApi.js';
import { LINE_ID, LINE_ADD_URL } from '../constants/contact.js';
import { analyticsEvent, gaItem } from '../utils/analytics.js';
import './CheckoutPage.css';

const STEPS = [
  { id: 1, name: '確認購物車', en: 'REVIEW' },
  { id: 2, name: '配送地址',   en: 'SHIPPING' },
  { id: 3, name: '送出訂單',   en: 'CONTACT' },
];

const SHIP_METHODS = [
  { id: '711',   name: '7-11 取貨',  desc: '2–4 個工作天' },
];

// 專人結帳：客服將透過所選方式聯繫，完全不在線上輸入卡號
const CONTACT_METHODS = [
  { id: 'line',    name: '加 MICHIDART 官方 LINE', sub: `${LINE_ID} · 最快、推薦`, badge: '推薦' },
  { id: 'line-id', name: '留下我的 LINE ID',        sub: '由客服主動加你' },
];

function CheckoutPage() {
  const { items, subtotal, clearCart } = useCart();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [step, setStep]   = useState(1);
  const [recipient, setRecipient] = useState({ name: '', phone: '', address: '' });
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [shipId, setShipId] = useState('711');
  const [contactId, setContactId] = useState('line');
  const [contactInfo, setContactInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkoutTracked, setCheckoutTracked] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsInput, setPointsInput] = useState(0); // 使用者想折抵的點數

  // 載入會員地址簿，有預設地址就自動帶入（使用者仍可改 / 換）
  useEffect(() => {
    getAddresses().then(list => {
      setSavedAddrs(list || []);
      const def = list?.find(a => a.isDefault) || list?.[0];
      if (def) setRecipient({ name: def.recipientName, phone: def.phone, address: def.address });
    }).catch(() => {});
  }, []);

  // 載入會員可用點數（供結帳折抵）
  useEffect(() => {
    getPoints().then(p => setPointsBalance(p?.balance ?? 0)).catch(() => {});
  }, []);

  const shipFee = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const payable = subtotal + shipFee;
  // 折抵上限 = min(餘額, 應付)；實際折抵夾在 0~上限，並取整（點數為整數，避免小數折抵）
  const maxRedeem = Math.min(pointsBalance, payable);
  const redeem = Math.max(0, Math.min(Math.floor(Number(pointsInput) || 0), maxRedeem));
  const total = payable - redeem;

  useEffect(() => {
    if (checkoutTracked || items.length === 0) return;
    analyticsEvent('begin_checkout', {
      currency: 'TWD',
      value: total,
      coupon: redeem > 0 ? 'points' : undefined,
      items: items.map(gaItem),
    });
    setCheckoutTracked(true);
  }, [checkoutTracked, items, total, redeem]);

  const handleNext = async () => {
    if (step === 1) {
      if (items.length === 0) { showToast('購物車是空的，請先加入商品', 'error'); return; }
      setStep(2);
      return;
    }
    if (step === 2) {
      // 收件資訊必填驗證（後端也會再驗一次）
      if (!recipient.name.trim() || !recipient.phone.trim() || !recipient.address.trim()) {
        showToast('請填寫收件人姓名、電話與地址', 'error');
        return;
      }
      setStep(3);
      return;
    }
    // 後端用 variantId 查真實價格下單；沒有 variantId 的品項（舊 mock / 未支援類型）無法成立訂單
    const unsupported = items.filter(i => !i.variantId);
    if (unsupported.length > 0) {
      showToast(`以下品項暫不支援線上下單：${unsupported.map(i => i.name).join('、')}`, 'error', 5000);
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        // 後端只認 type / variantId / qty（金額後端重算）；name / price / tags 是 mock 模式顯示用
        items: items.map(i => ({
          type: i.kind === 'virtual' ? 'virtual' : 'physical',
          variantId: i.variantId,
          qty: i.qty,
          name: i.name,
          price: i.price,
          tags: i.tags,
        })),
        recipientName: recipient.name.trim(),
        recipientPhone: recipient.phone.trim(),
        recipientAddress: recipient.address.trim(),
        shippingMethod: shipId,
        contactMethod: contactId,
        contactInfo: contactInfo.trim() || undefined,
        pointsToRedeem: redeem > 0 ? redeem : undefined,
      });
      analyticsEvent('purchase', {
        transaction_id: String(order.id),
        currency: 'TWD',
        value: Number(order.total ?? total),
        shipping: Number(order.shipping ?? shipFee),
        coupon: redeem > 0 ? 'points' : undefined,
        payment_type: contactId,
        items: items.map(gaItem),
      });
      clearCart();
      showToast(`訂單已送出：${order.id}`, 'success', 5000);
      navigate('/success', { state: { orderId: order.id } });
    } catch (err) {
      showToast('下單失敗：' + err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="v3-co">
      <h1>結帳 · CHECKOUT</h1>

      <VerifyEmailBanner />

      {/* Stepper */}
      <div className="v3-co-steps">
        {STEPS.map((s, i) => (
          <>
            <div
              key={s.id}
              className={`v3-co-step${step === s.id ? ' on' : ''}${step > s.id ? ' done' : ''}`}
            >
              <span className="num">{step > s.id ? '✓' : s.id}</span>
              <div><b>{s.name}</b><small>{s.en}</small></div>
            </div>
            {i < STEPS.length - 1 && (
              <div key={`line-${s.id}`} className={`v3-co-step-line${step > s.id ? ' done' : ''}`} />
            )}
          </>
        ))}
      </div>

      <div className="v3-co-grid">
        {/* Left: step content */}
        <section>
          {step === 1 && <StepReview items={items} />}
          {step === 2 && <StepShipping recipient={recipient} setRecipient={setRecipient} savedAddrs={savedAddrs} shipId={shipId} setShipId={setShipId} shipFee={shipFee} subtotal={subtotal} />}
          {step === 3 && <StepContact contactId={contactId} setContactId={setContactId} contactInfo={contactInfo} setContactInfo={setContactInfo} />}

          <div className="v3-co-actions">
            {step > 1 ? (
              <button className="v3-co-back" onClick={() => setStep(s => s - 1)}>
                <Icon name="arrow-l" size={16} /> 上一步
              </button>
            ) : (
              <button className="v3-co-back" onClick={() => navigate('/cart')}>
                <Icon name="arrow-l" size={16} /> 返回購物車
              </button>
            )}
            <button className="v3-co-next" onClick={handleNext} disabled={submitting}>
              {step < 3
                ? <>下一步 <Icon name="arrow" size={16} /></>
                : submitting ? '處理中…' : '送出訂單 · 待客服聯繫'
              }
            </button>
          </div>
        </section>

        {/* Right: sticky summary */}
        <OrderSummary
          items={items} subtotal={subtotal} shipFee={shipFee} total={total}
          pointsBalance={pointsBalance} maxRedeem={maxRedeem} redeem={redeem}
          pointsInput={pointsInput} setPointsInput={setPointsInput}
        />
      </div>
    </main>
  );
}

function StepReview({ items }) {
  if (items.length === 0) {
    return (
      <div className="v3-co-panel">
        <h3>購物車是空的</h3>
        <p style={{ color: 'var(--ink-soft)', fontSize: 14 }}>請先加入商品再結帳。</p>
      </div>
    );
  }
  return (
    <div className="v3-co-panel">
      <h3>確認商品 · 共 {items.reduce((s, i) => s + i.qty, 0)} 件</h3>
      {items.map(item => (
        <div className="co-review-item" key={item.lineId ?? item.id}>
          <div className="co-review-thumb">
            <ProductImage src={item.image} alt={item.name}
              imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div>
            <div className="co-review-name">{item.name}</div>
            <div className="co-review-var">
              {item.tags?.join(' · ')} · 數量 {item.qty}
            </div>
          </div>
          <span className="co-review-price">NT${(item.price * item.qty).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function StepShipping({ recipient, setRecipient, savedAddrs, shipId, setShipId, shipFee, subtotal }) {
  const set = (k, val) => setRecipient(r => ({ ...r, [k]: val }));
  const pick = (a) => setRecipient({ name: a.recipientName, phone: a.phone, address: a.address });
  return (
    <div className="v3-co-panel">
      <h3>收件資訊</h3>
      {savedAddrs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ font: '600 12px "Noto Sans TC", sans-serif', color: 'var(--ink-soft)', alignSelf: 'center' }}>從地址簿帶入：</span>
          {savedAddrs.map(a => (
            <button key={a.id} type="button" onClick={() => pick(a)}
              style={{ padding: '6px 12px', borderRadius: 999, border: '1.5px solid var(--line)', background: '#fff', font: '600 12px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>
              {a.label || a.recipientName}{a.isDefault ? ' · 預設' : ''}
            </button>
          ))}
        </div>
      )}
      <div className="co-new-addr">
        <div className="co-row2">
          <div className="co-field">
            <label>姓名 *</label>
            <input placeholder="收件人姓名" value={recipient.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="co-field">
            <label>電話 *</label>
            <input placeholder="09xx-xxx-xxx" value={recipient.phone} onChange={e => set('phone', e.target.value)} />
          </div>
        </div>
        <div className="co-field">
          <label>地址 *</label>
          <input placeholder="縣市 · 鄉鎮 · 詳細地址（店到店請填門市）" value={recipient.address} onChange={e => set('address', e.target.value)} />
        </div>
      </div>

      <h3 style={{ marginTop: 24 }}>配送方式</h3>
      <div className="v3-ship-row">
        {SHIP_METHODS.map(s => (
          <div key={s.id} className={`v3-ship${shipId === s.id ? ' on' : ''}`} onClick={() => setShipId(s.id)}>
            <b>{s.name}</b>
            <small>{s.desc} · {shipFee === 0 ? '免運' : `NT$${shipFee}`}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepContact({ contactId, setContactId, contactInfo, setContactInfo }) {
  return (
    <div className="v3-co-panel">
      <h3>送出訂單 · 專人為你服務</h3>
      <div className="v3-contact-note">
        <Icon name="shield" size={18} />
        <p>本站目前採<b>專人結帳</b>服務。送出訂單後，MICHIDART 客服會在<b>營業時間 1 小時內</b>透過你選擇的方式聯繫你，確認商品、金額與付款方式（轉帳 / 面交 / 貨到付款），不需要在線上輸入卡號。</p>
      </div>

      <h3 style={{ marginTop: 24 }}>希望客服怎麼聯繫你？</h3>
      <div className="v3-pay">
        {CONTACT_METHODS.map(p => (
          <div key={p.id} className={`v3-pay-item${contactId === p.id ? ' on' : ''}`} onClick={() => setContactId(p.id)}>
            <span className="radio" />
            <div><b>{p.name}</b><small>{p.sub}</small></div>
            {p.badge && <span className="v3-pay-badge">{p.badge}</span>}
          </div>
        ))}
      </div>

      {contactId === 'line' && (
        <div className="v3-contact-cta">
          <b>加入 {LINE_ID} 官方帳號</b>
          <small>送出訂單後，把訂單編號貼到聊天室，客服會立即為你處理。</small>
          <button type="button" onClick={() => window.open(LINE_ADD_URL, '_blank', 'noopener,noreferrer')}
            style={{ display: 'inline-block', marginTop: 10, background: '#06C755', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', font: '700 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>
            加入官方 LINE
          </button>
        </div>
      )}
      {contactId === 'line-id' && (
        <div className="v3-card-form">
          <div className="full">
            <label>你的 LINE ID</label>
            <input placeholder="輸入你的 LINE ID，客服會主動加你"
              value={contactInfo} onChange={e => setContactInfo(e.target.value)} />
          </div>
        </div>
      )}

      <label className="v3-contact-agree">
        <input type="checkbox" defaultChecked />
        <span>我了解這是<b>專人結帳</b>，送出後將由客服聯繫完成付款，本步驟不會線上扣款。</span>
      </label>
    </div>
  );
}

function OrderSummary({ items, subtotal, shipFee, total, pointsBalance, maxRedeem, redeem, pointsInput, setPointsInput }) {
  return (
    <aside className="v3-co-sum">
      <h3>訂單摘要</h3>
      <div className="sum-items">
        {items.map(item => (
          <div className="sum-item" key={item.lineId ?? item.id}>
            <div className="sum-pic">
              <ProductImage src={item.image} alt={item.name}
                imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div>
              <div className="sum-name">{item.name}</div>
              <span className="sum-var">{item.tags?.join(' · ')} × {item.qty}</span>
            </div>
            <span className="sum-price">NT${(item.price * item.qty).toLocaleString()}</span>
          </div>
        ))}
      </div>
      <div className="sum-line"><span>商品總額</span><b>NT${subtotal.toLocaleString()}</b></div>
      <div className="sum-line"><span>運費 · 7-11 取貨</span><b>{shipFee === 0 ? '免運' : `NT$${shipFee}`}</b></div>

      {/* 紅利點數折抵（1 點 = NT$1）：有餘額才顯示 */}
      {pointsBalance > 0 && (
        <div className="sum-points">
          <div className="sum-points-head">
            <span>紅利點數折抵</span>
            <small>可用 {pointsBalance.toLocaleString()} P</small>
          </div>
          <div className="sum-points-row">
            <input
              type="number" min="0" max={maxRedeem} step="1"
              value={pointsInput}
              onChange={e => setPointsInput(e.target.value)}
              placeholder="0"
              aria-label="使用點數"
            />
            <button type="button" onClick={() => setPointsInput(maxRedeem)}>用好用滿</button>
          </div>
          {redeem > 0 && (
            <div className="sum-line discount"><span>已折抵 {redeem.toLocaleString()} 點</span><b>−NT${redeem.toLocaleString()}</b></div>
          )}
        </div>
      )}

      <div className="sum-total">
        <b>合計</b>
        <span className="amt"><small>NT$</small>{total.toLocaleString()}</span>
      </div>
    </aside>
  );
}

export default CheckoutPage;
